import type {
	Crafts,
	StatusCode,
	TypeCode,
} from "@/pages/work-orders/models/Filters";
import type { WorkOrder } from "@/pages/work-orders/models/WorkOrder";
import { INITIAL_VALUES_MODAL } from "@/shared/work-orders-modals/models/ModalInitialValue";
import type { ModalWorkOrder } from "@/shared/work-orders-modals/models/ModalWorkOrder";
import { createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import type { ColDef } from "ag-grid-community";
import type { Asset } from "../models/Asset";
import type { AssetAttachment } from "../models/AssetAttachment";
import type { AssetDocument } from "../models/AssetDocument";
import type { AssetLOTO } from "../models/AssetLOTO";
import type { AssetPart } from "../models/AssetPart";
import type { Statistic } from "../models/Statistic";
import type { TextResource } from "../models/TextResource";
import { DocumentParameters } from "@/pages/drawings/search-documents/models/DocumentParameters";
import { METADATA_INITIAL_VALUES } from "@/pages/drawings/search-documents/models/metaDataInitialValue";

export const fromDate: Date = new Date();
fromDate.setDate(1);
fromDate.setMonth(0);
fromDate.setFullYear(new Date().getFullYear());

const AssetDetailsInitialState: {
	asset: Asset[];

	assetSearchId: number | string | undefined;

	gridValue: number;

	currentGridName: string;

	attachmentsColumns: string[];

	textResourceRows: TextResource[];
	textResourceColumns: string[];

	statisticsRows: Statistic[];
	statisticsColumns: string[];

	workOrdersRows: WorkOrder[];
	workOrdersColumns: string[];

	workOrderSelectedRow: ModalWorkOrder;

	assetsTable: boolean;

	statisticsTable: boolean;

	workOrdersTable: boolean;

	firstTime: boolean;

	assetSearchName: string;

	allAssets: Asset[];

	listAllDocuments: { [DocumentNumber: string]: AssetDocument };

	listAllAttachments: { [attachment: string]: AssetAttachment };

	listAssetDocuments: AssetDocument[];

	listAssetAttachments: AssetAttachment[];

	listAssetParts: AssetPart[];

	listAssetLOTO: AssetLOTO[];

	documentSearch: string;

	attachmentSearch: string;

	partSearch: string;

	LOTONumberToLink: string;

	LOTONameToLink: string;

	showListOfDocuments: boolean;

	showListOfAttachments: boolean;

	showListOfParts: boolean;

	dataDocumentSelected: AssetDocument | null;

	dataAttachmentSelected: AssetAttachment | null;

	listAttachmentSelected: any[] | null;

	dataPartSelected: AssetPart | null;

	dataLOTOSelected: AssetLOTO | null;

	dataMarkDownText: string | null;

	editMarkDownTextMood: boolean;

	assetDocumentComments: string;

	assetAttachmentComments: string;

	assetPartComments: string;

	assetLOTOComments: string;

	openAddCommentDialog: boolean;

	loaderListSearchDocuments: boolean;

	loaderListSearchAttachments: boolean;

	loaderListSearchParts: boolean;

	rowSelected: any;
	statusCodes: StatusCode[];

	crafts: Crafts[];

	typeCodes: TypeCode[];

	// WO
	lastNWorkOrders: number;

	SearchText: string | null;

	TypeCode: string | null;

	WorkOrderStatusCode: string | null;

	CraftId: number | string | null;

	uploadFileDialog: boolean;

	assetFullPath: string | null;

	rowsGrid: Record<string, unknown>[];

	columnsGrid: ColDef[];

	loadingDataGrid: boolean;

	DocumentMetadata: DocumentParameters;

	displayViewerAttachment: boolean;
} = {
	asset: [],

	assetSearchId: 0,

	gridValue: 1,
	currentGridName: "",

	attachmentsColumns: [],

	textResourceRows: [],
	textResourceColumns: [],

	statisticsRows: [],
	statisticsColumns: [],

	workOrdersRows: [],
	workOrdersColumns: [],

	workOrderSelectedRow: INITIAL_VALUES_MODAL,

	assetsTable: true,

	statisticsTable: true,

	workOrdersTable: true,

	firstTime: true,

	assetSearchName: "Plant",

	allAssets: [],

	listAllDocuments: {},

	listAllAttachments: {},

	listAssetDocuments: [],

	listAssetAttachments: [],

	listAttachmentSelected: [],

	listAssetParts: [],

	listAssetLOTO: [],

	dataMarkDownText: "",

	editMarkDownTextMood: false,

	assetDocumentComments: "",

	assetAttachmentComments: "",

	assetPartComments: "",

	assetLOTOComments: "",

	openAddCommentDialog: false,

	documentSearch: "",

	attachmentSearch: "",

	partSearch: "",

	LOTONumberToLink: "",

	LOTONameToLink: "",

	loaderListSearchDocuments: false,

	loaderListSearchAttachments: false,

	loaderListSearchParts: false,

	showListOfDocuments: false,

	showListOfAttachments: false,

	showListOfParts: false,

	dataDocumentSelected: null,

	dataAttachmentSelected: null,

	dataPartSelected: null,

	dataLOTOSelected: null,

	rowSelected: undefined,

	statusCodes: [],

	crafts: [],

	typeCodes: [],

	uploadFileDialog: false,
	// WO
	lastNWorkOrders: 20,

	SearchText: "",

	TypeCode: "A",

	WorkOrderStatusCode: "O",

	CraftId: "A",

	assetFullPath: null,

	rowsGrid: [],

	columnsGrid: [],

	loadingDataGrid: false,

	DocumentMetadata: METADATA_INITIAL_VALUES,

	displayViewerAttachment: false,
};

export const AssetDetailsReducer = createSlice({
	name: "__",
	initialState: AssetDetailsInitialState,
	reducers: {
		setAsset(state, { payload }) {
			state.asset = payload;
		},
		setAssetSearchId(state, { payload }) {
			state.assetSearchId = payload;
		},
		setGridValue(state, { payload }) {
			state.gridValue = payload;
		},
		setCurrentGridName(state, { payload }) {
			state.currentGridName = payload;
		},

		// ======================== FILTERS =============================

		setStatusCodesWorkOrders(state, { payload }) {
			state.statusCodes = payload;
		},

		setCraftsWorkOrders(state, { payload }) {
			state.crafts = payload;
		},

		setTypesCodesWorkOrders(state, { payload }) {
			state.typeCodes = payload;
		},

		setLastNWorkOrders(state, { payload }) {
			state.lastNWorkOrders = payload;
		},

		setSearchTextWorkOrders(state, { payload }) {
			state.SearchText = payload;
		},

		setTypeCodeWorkOrders(state, { payload }) {
			state.TypeCode = payload;
		},

		setStatusCodeWorkOrders(state, { payload }) {
			state.WorkOrderStatusCode = payload;
		},

		setCraftIdCodeWorkOrders(state, { payload }) {
			state.CraftId = payload;
		},

		// ==============================================================
		// ========================= Parts ==============================

		setListAssetParts(state, { payload }) {
			state.listAssetParts = payload;
		},
		setLoaderListSearchParts(state, { payload }) {
			state.loaderListSearchParts = payload;
		},
		setPartSearch(state, { payload }) {
			state.partSearch = payload;
		},
		setShowListOfParts(state, { payload }) {
			state.showListOfParts = payload;
		},
		setDataPartSelected(state, { payload }) {
			state.dataPartSelected = payload;
		},
		setAssetPartComments(state, { payload }) {
			state.assetPartComments = payload;
		},
		// ========================= Documents ===========================

		setListAllDocuments(state, { payload }) {
			state.listAllDocuments = payload;
		},

		setListAssetDocuments(state, { payload }) {
			state.listAssetDocuments = payload;
		},

		setAssetDocumentComments(state, { payload }) {
			state.assetDocumentComments = payload;
		},

		setOpenAddCommentDialog(state, { payload }) {
			state.openAddCommentDialog = payload;
		},

		setDocumentSearch(state, { payload }) {
			state.documentSearch = payload;
		},

		setLoaderListSearchDocuments(state, { payload }) {
			state.loaderListSearchDocuments = payload;
		},

		setShowListOfDocuments(state, { payload }) {
			state.showListOfDocuments = payload;
		},

		setDataDocumentSelected(state, { payload }) {
			state.dataDocumentSelected = payload;
		},

		setRowSelected(state, { payload }) {
			state.rowSelected = payload;
		},

		setDocumentMetadata(state, { payload }) {
			state.DocumentMetadata = payload;
		},
		setVersionDocumentMetadata(state, { payload }) {
			state.DocumentMetadata.Version = payload;
		},
		setExtensionDocumentMetadata(state, { payload }) {
			state.DocumentMetadata.Extension = payload;
		},
		setUploadTimestampDocumentMetadata(state, { payload }) {
			state.DocumentMetadata.UploadTimestamp = payload;
		},
		setUserNameDocumentMetadata(state, { payload }) {
			state.DocumentMetadata.UserName = payload;
		},

		// ========================= LOTO ===========================

		setListAssetLOTO(state, { payload }) {
			state.listAssetLOTO = payload;
		},
		setDataLOTOSelected(state, { payload }) {
			state.dataLOTOSelected = payload;
		},
		setAssetLOTOComments(state, { payload }) {
			state.assetLOTOComments = payload;
		},
		setLOTONumberToLink(state, { payload }) {
			state.LOTONumberToLink = payload;
		},
		setLOTONameToLink(state, { payload }) {
			state.LOTONameToLink = payload;
		},
		// ========================= Attachments ========================
		setListAssetAttachments(state, { payload }) {
			state.listAssetAttachments = payload;
		},

		setDataAttachmentSelected(state, { payload }) {
			state.dataAttachmentSelected = payload;
		},

		setListAttachmentSelected(state, { payload }) {
			state.listAttachmentSelected = payload;
		},

		setListAllAttachments(state, { payload }) {
			state.listAllAttachments = payload;
		},

		setAttachmentSearch(state, { payload }) {
			state.attachmentSearch = payload;
		},
		setLoaderListSearchAttachments(state, { payload }) {
			state.loaderListSearchAttachments = payload;
		},

		setShowListOfAttachments(state, { payload }) {
			state.showListOfAttachments = payload;
		},

		setAssetAttachmentComments(state, { payload }) {
			state.assetAttachmentComments = payload;
		},

		setUploadFileDialog(state, { payload }) {
			state.uploadFileDialog = payload;
		},
		setDisplayViewerAttachment(state, { payload }) {
			state.displayViewerAttachment = payload;
		},
		// ========================= TEXT ======================
		setDataMarkDownText(state, { payload }) {
			state.dataMarkDownText = payload;
		},
		setEditMarkDownTextMood(state, { payload }) {
			state.editMarkDownTextMood = payload;
		},

		// ========================= GRID ======================
		setRowsGrid(state, { payload }) {
			state.rowsGrid = payload;
		},
		setColumnsGrid(state, { payload }) {
			state.columnsGrid = payload;
		},
		setLoadingDataGrid(state, { payload }) {
			state.loadingDataGrid = payload;
		},

		// ========================= Statistics =========================
		setStatisticsRows(state, { payload }) {
			state.statisticsRows = payload;
		},
		setStatisticsColumns(state, { payload }) {
			state.statisticsColumns = payload;
		},
		setStatisticsTable(state, { payload }) {
			state.statisticsTable = payload;
		},
		// ========================= Work Orders ======================
		setWorkOrdersRows(state, { payload }) {
			state.workOrdersRows = payload;
		},
		setWorkOrdersColumns(state, { payload }) {
			state.workOrdersColumns = payload;
		},
		setWorkOrdersTable(state, { payload }) {
			state.workOrdersTable = payload;
		},
		setWorkOrderSelectedRow(state, { payload }) {
			state.workOrderSelectedRow = payload;
		},
		// ====================================================
		setAssetsTable(state, { payload }) {
			state.assetsTable = payload;
		},

		setFirstTime(state, { payload }) {
			state.firstTime = payload;
		},

		setAssetSearchName(state, { payload }) {
			state.assetSearchName = payload;
		},

		setallAssets(state, { payload }) {
			state.allAssets = payload;
		},

		setAssetFullPath(state, { payload }) {
			state.assetFullPath = payload;
		},
	},
});
